import * as React from "react"
import "./Animations.scss"

import { CSSTransition } from "react-transition-group"

interface IAnimationsConfig {
	timeout?: number
	classIn?: string
	classOut?: string
}

type IPresets = "fade" | "slide-in-out-bottom" | "scale-in-out-br"

interface IProps {
	className?: string
	children?: React.ReactNode
	onExited?: () => void
	onFinish?: () => void
	onAnimationStart?: () => void
	ref: React.Ref<HTMLDivElement>
	presets?: IPresets
	show?: boolean
	unmountOnExit?: boolean
}

const Animations = React.forwardRef<HTMLDivElement, IProps>((props, ref) => {
	const [show, setShow] = React.useState(false)

	const animationsConfig = React.useRef<IAnimationsConfig>({})
	const animationFinishedTimer = React.useRef<NodeJS.Timeout>()

	switch (props.presets) {
		case "slide-in-out-bottom": {
			animationsConfig.current.timeout = 500
			animationsConfig.current.classIn = "slide-in-bottom"
			animationsConfig.current.classOut = "slide-out-bottom"
			break
		}

		case "scale-in-out-br": {
			animationsConfig.current.timeout = 300
			animationsConfig.current.classIn = "scale-in-br"
			animationsConfig.current.classOut = "scale-out-br"
			break
		}

		default: {
			animationsConfig.current.timeout = 250
			animationsConfig.current.classIn = "fade-in"
			animationsConfig.current.classOut = "fade-out"
		}
	}

	const loadFinishTimer = React.useCallback(() => {
		animationFinishedTimer.current &&
			clearTimeout(animationFinishedTimer.current)

		animationFinishedTimer.current = setTimeout(() => {
			props.onFinish && props.onFinish()
		}, animationsConfig.current.timeout)
	}, [animationFinishedTimer.current])

	React.useEffect(() => {
		if (props.show === undefined) {
			setShow(true)
			loadFinishTimer()
		} else {
			setShow(props.show)
			props.show && loadFinishTimer()
		}
	}, [props.show])

	return (
		<CSSTransition
			in={show}
			nodeRef={ref}
			timeout={animationsConfig.current.timeout}
			onEnter={props.onAnimationStart}
			onExited={props.onExited}
			unmountOnExit={
				props.unmountOnExit === undefined ? true : props.unmountOnExit
			}
			classNames={{
				enter: animationsConfig.current.classIn,
				enterActive: animationsConfig.current.classIn,
				enterDone: animationsConfig.current.classIn,
				exit: animationsConfig.current.classOut,
				exitActive: animationsConfig.current.classOut,
				exitDone: animationsConfig.current.classOut,
			}}
		>
			{props.children}
		</CSSTransition>
	)
})

export default React.memo(Animations)
